import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';
import { logError } from '@office-start/telemetry-service';
import { KnownRequestInstrumentationEvents } from '@office-start/telemetry-types/dist';
import { filter, take } from 'rxjs/operators';
import { ConsumerLicensingData } from '../../first-run/shared/first-run-data.service';
import { HttpStatusCode } from '../constants/http-status-code.enum';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpPromise } from '../instrumentation/instrumented-http.service';
import { HttpRequestOptions } from '../models/http-request-options.model';
import { ObservableScope } from '../models/observable-scope.enum';
import { AccessTokenConsumer, AccessTokenService } from '../services/access-token.service';
import { ConfigParserService } from '../services/config-parser.service';
import { EventService } from '../services/event.service';
import { OfficeHomeApiConfigService } from '../services/officehome-api-config.service';
import { SchedulerService } from '../services/scheduler.service';
import { XhrService } from '../services/xhr.service';
import { DOMInteractiveMarker } from '../utilities/dom-interactive-marker.utility';
import { AngularEvents } from './angular-events';
import { CapabilitiesService } from './capabilities.service';
import { AuthType, ClientStartupConfigService } from './client-startup-config.service';
import { ContextService } from './context.service';

export interface UserConsentConfig {
    userConsent: ConsumerUserConsentGroup;
    status: ConsumerUserConsentDataStatus;
}

export interface WorkloadPublicDataConfig {
    userConsentInfo: UserConsentConfig;
    hasBusinessLicense: boolean;
}

export interface ConsumerWorkloadPublicData {
    userConsent: ConsumerUserConsentGroup;
    hasBusinessLicense: boolean;
    licensingData: ConsumerLicensingData;
}

export const enum ConsumerUserConsentGroup {
    CanConsent,
    PartialConsent,
    CannotConsent
}

const enum ConsumerUserConsentDataStatus {
    DataPresent,
    DataNotPresent,
    ServiceFailure
}

@Injectable({
    providedIn: 'root'
})
export class WorkloadPublicDataConfigService extends XhrService {
    private configPromise: Promise<WorkloadPublicDataConfig>;
    private readonly consumerWorkloadPublicDataEndpoint = '/api/workloadpublicdata/consumer';
    private readonly consumerWorkloadPublicDataForceRefreshQuery = 'forceRefresh=true';
    private readonly requestConfig: HttpRequestOptions = {
        headers: new Map([
            ['Accept', 'application/json']
        ])
    };

    constructor(
        accessTokenService: AccessTokenService,
        contextService: ContextService,
        instrumentedHttp: InstrumentedHttp,
        instrumentationService: InstrumentationService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        platformLocation: PlatformLocation,
        scheduler: SchedulerService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly configParserService: ConfigParserService,
        private readonly startupConfigService: ClientStartupConfigService,
        private readonly eventService: EventService
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp, AccessTokenConsumer.OfficeHome, contextService, officeHomeApiConfigService, platformLocation, scheduler);
    }

    public getConfig(): Promise<WorkloadPublicDataConfig> {
        if (!this.configPromise) {
            if (this.startupConfigService.config.authType !== AuthType.MSA) {
                return Promise.reject();
            }

            if (this.capabilitiesService.capabilities.isPwaInUwp) { // if PWA, we turn off all features
                this.configPromise = Promise.resolve(this.configParserService.parse<WorkloadPublicDataConfig>('workloadPublicData'));
            } else {
                const tokensLoadedPromise = this.eventService.events.withScope(ObservableScope.PastAndFuture).pipe(
                    filter(event => event.name === AngularEvents.primaryTokensLoaded),
                    take(1))
                    .toPromise();
                const domReadyPromise = DOMInteractiveMarker.fullPageReady();
                this.configPromise = Promise.race([tokensLoadedPromise, domReadyPromise]).then(() => this.configParserService.parse<WorkloadPublicDataConfig>('workloadPublicData'));
            }
        }
        return this.configPromise;
    }

    public forceRefreshAndGetWorkloadPublicData() {
        const pr: Promise<ConsumerWorkloadPublicData> = new Promise((resolve, reject) => {
            const eventName = KnownInstrumentationEvents.OfficeConsumerWorkloadPublicDataFetch;
            this.get<ConsumerWorkloadPublicData>(eventName, KnownRequestInstrumentationEvents.OfficeConsumerWorkloadPublicDataFetch,
            this.contextService.sharedContext.officeHomeApiRoot + this.consumerWorkloadPublicDataEndpoint + '?' + this.consumerWorkloadPublicDataForceRefreshQuery,
            (promise: InstrumentedHttpPromise<ConsumerWorkloadPublicData>) => {
                promise.instrument((result, params) => {
                    if (result.ok) {
                        params[InstrumentationParam.HttpCode] = HttpStatusCode.OK.toString();
                    } else {
                        params[InstrumentationParam.HttpCode] = HttpStatusCode.UnexpectedResponse.toString();
                    }
                }).then(response => {
                    resolve(response.data);
                });
            }, error => {
                const message = `Failed to get consumer workload public data with error: ${error}`;
                logError({
                    eventName,
                    message
                });
                reject(error);
            },
            this.requestConfig,
            FetchStrategy.NetworkOnly);
        });
        return pr;
    }
}
